// types/avatar.ts
import { Timestamp } from 'firebase/firestore';

// Usando suas poses existentes
export type AvatarPose = 'meditation' | 'pray' | 'laydown' | 'eyes_closed' | 'hug' | 'kneel' | 'bow' | 'head_down' | 'standing';

// Usando suas direções existentes  
export type AvatarDirection = 'front' | 'back' | 'left' | 'right';

export const POSE_TRANSLATIONS: { [key in AvatarPose]: string } = {
  meditation: 'Sentado',
  pray: 'Oração',
  laydown: 'Deitado',
  eyes_closed: 'Olhos Fechados',
  hug: 'Abraço',
  kneel: 'Ajoelhado',
  bow: 'Reverência',
  head_down: 'Cabeça Baixa',
  standing: 'De Pé'
};

// NOVO: Dicionário de tradução para as direções
export const DIRECTION_TRANSLATIONS: { [key in AvatarDirection]: string } = {
  front: 'Frente',
  back: 'Costas',
  left: 'Esquerda',
  right: 'Direita'
};

// Usando suas configurações existentes
export interface AvatarConfig {
  id: string;
  name: string;
  folderName: string; // ex: 'avatar_1', 'avatar_2'
  thumbnail: string; // para preview no seletor
}

// Expandindo sua interface AvatarState para incluir campos do Firestore
export interface AvatarState {
  id: string; // userId do Firebase
  username: string;
  avatarId: string; // qual avatar foi escolhido
  pose: AvatarPose;
  direction: AvatarDirection;
  x: number;
  y: number;
  isCurrentUser: boolean; // Calculado no cliente
  role?: 'terapeuta' | 'paciente' | 'participante'; // Adicionado para sincronização
  lastUpdated?: Date; // Adicionado para sincronização
  
  // ✅ NOVAS PROPRIEDADES: Para controle por terapeuta
  lastControlledBy?: string;        // UID do usuário que fez a última alteração
  lastControlledByName?: string;    // Nome do usuário que fez a última alteração  
  lastControlledAt?: Date;          // Timestamp da última alteração por terceiro
}

// Suas configurações existentes
export const AVAILABLE_AVATARS: AvatarConfig[] = [
  {
    id: 'avatar1',
    name: 'Avatar 1',
    folderName: 'avatar_1',
    thumbnail: '/assets/avatars/avatar_1/perfil.png' // Ajustado para pose padrão
  },
  {
    id: 'avatar2', 
    name: 'Avatar 2',
    folderName: 'avatar_2',
    thumbnail: '/assets/avatars/avatar_2/perfil.png'
  },
  {
    id: 'avatar3',
    name: 'Avatar 3',
    folderName: 'avatar_5',
    thumbnail: '/assets/avatars/avatar_5/perfil.png'
  },
  {
    id: 'avatar4',
    name: 'Avatar 4',
    folderName: 'avatar_6',
    thumbnail: '/assets/avatars/avatar_6/perfil.png'
  },
  {
    id: 'avatar5',
    name: 'Avatar 5',
    folderName: 'avatar_7',
    thumbnail: '/assets/avatars/avatar_7/perfil.png'
  },
  {
    id: 'avatar6',
    name: 'Avatar 6',
    folderName: 'avatar_8',
    thumbnail: '/assets/avatars/avatar_8/perfil.png'
  },
  {
    id: 'avatar7',
    name: 'Avatar 7',
    folderName: 'avatar_9',
    thumbnail: '/assets/avatars/avatar_9/perfil.png'
  },
  {
    id: 'avatar8',
    name: 'Avatar 8',
    folderName: 'avatar_10',
    thumbnail: '/assets/avatars/avatar_10/perfil.png'
  },
  {
    id: 'avatar9',
    name: 'Avatar 9',
    folderName: 'avatar_11',
    thumbnail: '/assets/avatars/avatar_11/perfil.png'
  },
  {
    id: 'avatar10',
    name: 'Avatar 10',
    folderName: 'avatar_12',
    thumbnail: '/assets/avatars/avatar_12/perfil.png'
  },
  {
    id: 'avatar11',
    name: 'Avatar 11',
    folderName: 'avatar_13',
    thumbnail: '/assets/avatars/avatar_13/perfil.png'
  },
  {
    id: 'avatar12',
    name: 'Avatar 12',
    folderName: 'avatar_14',
    thumbnail: '/assets/avatars/avatar_14/perfil.png'
  },
  {
    id: 'avatar13',
    name: 'Avatar 13',
    folderName: 'avatar_15',
    thumbnail: '/assets/avatars/avatar_15/perfil.png'
  },
  {
    id: 'avatar14',
    name: 'Avatar 14',
    folderName: 'avatar_3',
    thumbnail: '/assets/avatars/avatar_3/perfil.png'
  },
  {
    id: 'avatar15',
    name: 'Avatar 15',
    folderName: 'avatar_4',
    thumbnail: '/assets/avatars/avatar_4/perfil.png'
  },
  {
    id: 'avatar16',
    name: 'Avatar 16',
    folderName: 'avatar_16',
    thumbnail: '/assets/avatars/avatar_16/perfil.png'
  }
];

// Suas poses existentes
export const AVAILABLE_POSES: AvatarPose[] = [
  'meditation',
  'hug',
  'laydown',
  'head_down',
  'eyes_closed',
  'pray',
  'kneel',
  'bow',
  'standing'
];

// CORREÇÃO: Adicionada a exportação que faltava
export const AVAILABLE_DIRECTIONS: AvatarDirection[] = [
    'front',
    'back',
    'left',
    'right'
];

// Suas direções existentes
export const POSE_DIRECTIONS_MAP: { [key in AvatarPose]: AvatarDirection[] } = {
    meditation: ['front', 'back', 'left', 'right'],
    pray: ['front', 'back', 'left', 'right'],
    laydown: ['left', 'right'], // A pose 'Deitado' só permite esquerda e direita
    eyes_closed: ['front', 'back', 'left', 'right'],
    hug: ['front', 'back', 'left', 'right'],
    kneel: ['front', 'back', 'left', 'right'],
    bow: ['front', 'back', 'left', 'right'],
    head_down: ['front', 'back', 'left', 'right'],
    standing: ['front', 'back', 'left', 'right']
};

// ✅ ATUALIZADA: Interface para dados do avatar no Firestore
export interface AvatarFirestoreData {
  userId: string;
  username: string;
  avatarId: string;
  pose: AvatarPose;
  direction: AvatarDirection;
  x: number;
  y: number;
  isOnline: boolean;
  role: 'terapeuta' | 'paciente' | 'participante';
  roomId: string;
  lastUpdated: Timestamp;
  
  // ✅ NOVAS PROPRIEDADES: Para controle por terapeuta no Firestore
  lastControlledBy?: string;
  lastControlledByName?: string;
  lastControlledAt?: Timestamp;
  lastSeen?: Timestamp; // Para heartbeat
  leftAt?: Timestamp; // Quando saiu da sala
  disconnectedAt?: Timestamp; // Quando desconectou
}

// Interface para dados do usuário no contexto de auth
export interface User {
  uid: string;
  email?: string;
  name?: string;
  role?: 'terapeuta' | 'paciente' | 'participante';
  avatarId?: string; // ID visual escolhido pelo usuário
}

// Interface para dados da sala
export interface Room {
  id: string;
  name: string;
  description?: string;
  createdBy: string; // userId do criador
  createdAt: Timestamp;
  isActive: boolean;
  maxParticipants?: number;
  participants: string[]; // array de userIds
}

// Sua função utilitária existente para gerar o caminho da imagem
export const getAvatarImagePath = (
  avatarId: string, 
  pose: AvatarPose, 
  direction: AvatarDirection
): string => {
  const avatar = AVAILABLE_AVATARS.find(a => a.id === avatarId);
  if (!avatar) {
    console.warn(`Avatar ${avatarId} not found`);
    return AVAILABLE_AVATARS[0].thumbnail; // fallback
  }
  
  return `/assets/avatars/${avatar.folderName}/${direction}_${pose}.png`;
};

// Sua função existente para obter dados do avatar por ID
export const getAvatarConfig = (avatarId: string): AvatarConfig | null => {
  return AVAILABLE_AVATARS.find(a => a.id === avatarId) || null;
};

// ✅ ATUALIZADA: Funções para conversão com Firestore
export const convertFirestoreToAvatarState = (
  firestoreData: AvatarFirestoreData,
  currentUserId: string
): AvatarState => {
  return {
    id: firestoreData.userId,
    username: firestoreData.username,
    avatarId: firestoreData.avatarId,
    pose: firestoreData.pose,
    direction: firestoreData.direction,
    x: firestoreData.x,
    y: firestoreData.y,
    isCurrentUser: firestoreData.userId === currentUserId,
    role: firestoreData.role,
    lastUpdated: firestoreData.lastUpdated?.toDate(),
    // ✅ NOVAS PROPRIEDADES: Controle por terapeuta
    lastControlledBy: firestoreData.lastControlledBy,
    lastControlledByName: firestoreData.lastControlledByName,
    lastControlledAt: firestoreData.lastControlledAt?.toDate(),
  };
};

export const convertAvatarStateToFirestore = (
  avatarState: Partial<AvatarState>,
  userId: string,
  roomId: string
): Partial<AvatarFirestoreData> => {
  return {
    userId,
    roomId,
    username: avatarState.username,
    avatarId: avatarState.avatarId,
    pose: avatarState.pose,
    direction: avatarState.direction,
    x: avatarState.x,
    y: avatarState.y,
    isOnline: true,
    role: avatarState.role,
    lastUpdated: Timestamp.now(),
    // ✅ NOVAS PROPRIEDADES: Controle por terapeuta
    lastControlledBy: avatarState.lastControlledBy,
    lastControlledByName: avatarState.lastControlledByName,
    lastControlledAt: avatarState.lastControlledAt ? Timestamp.fromDate(avatarState.lastControlledAt) : undefined,
  };
};

// Validações
export const isValidAvatarPose = (pose: string): pose is AvatarPose => {
  return AVAILABLE_POSES.includes(pose as AvatarPose);
};

export const isValidAvatarDirection = (direction: string): direction is AvatarDirection => {
  return AVAILABLE_DIRECTIONS.includes(direction as AvatarDirection);
};

export const isValidCoordinate = (coordinate: number): boolean => {
  return typeof coordinate === 'number' && 
         coordinate >= 0 && 
         coordinate <= 2000 && // limite máximo da tela
         !isNaN(coordinate);
};

// ✅ NOVA FUNÇÃO: Validar se usuário pode controlar avatar
export const canControlAvatar = (
  currentUserRole: string | undefined, 
  targetUserId: string, 
  currentUserId: string
): boolean => {
  // Usuário sempre pode controlar o próprio avatar
  if (targetUserId === currentUserId) return true;
  
  // Apenas terapeutas podem controlar avatares de outros
  return currentUserRole === 'terapeuta';
};

// Constants para configuração
export const AVATAR_CONFIG = {
  MOBILE_WIDTH: 120,
  MOBILE_HEIGHT: 160,
  DESKTOP_WIDTH: 180,
  DESKTOP_HEIGHT: 240,
  MAX_USERNAME_LENGTH: 20,
  DEFAULT_AVATAR_ID: 'avatar1', // Usando seu padrão
  DEFAULT_POSE: 'standing' as AvatarPose, // Usando sua pose padrão
  DEFAULT_DIRECTION: 'front' as AvatarDirection,
  MOBILE_BREAKPOINT: 768,
  
  // ✅ NOVAS CONFIGURAÇÕES: Para controle por terapeuta
  THERAPIST_CONTROL_TIMEOUT: 5000, // 5 segundos para mostrar indicador de controle
  HEARTBEAT_INTERVAL: 30000, // 30 segundos para heartbeat
} as const;
